import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InventoryItem, RoastedBulkGroup, RoastedBulkGroupItem } from '../../types/inventory';
import type { CuppingProfile } from '../../services/profileService';
import { inventoryService } from '../../services/inventoryService';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatCurrency, formatDecimal } from '../../utils/format';
import { InventoryRoastProfileButton } from './InventoryRoastProfileButton';
import { ProfileManager } from './ProfileManager';
import { History } from 'lucide-react';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastedBulkGroupItemsTableProps {
  group: RoastedBulkGroup;
  statusFilter: 'all' | 'active' | 'inactive';
  currencySymbol: string;
  items: InventoryItem[];
  onEditItem?: (itemId: string) => void;
  onOpenHistory: (itemId: string) => void;
  compact?: boolean;
  onOpenRoastProfile: (context: { itemId: string; itemName: string; workOrderId?: string }) => void;
  canWrite?: boolean;
}

export const RoastedBulkGroupItemsTable: React.FC<RoastedBulkGroupItemsTableProps> = ({
  group,
  statusFilter,
  currencySymbol,
  items,
  onEditItem,
  onOpenHistory,
  compact,
  onOpenRoastProfile,
  canWrite,
}) => {
  const { t } = useLanguage();
  const statusByItemId = new Map(items.map(item => [item.id, item.status]));

  const renderItemStatus = (status?: string) => (
    <span
      className={`px-2 uppercase inline-flex text-xs leading-5 font-semibold rounded-lg ${
        status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
      }`}
    >
      {status === 'active' ? t('forms.common.statusActive') : t('forms.common.statusInactive')}
    </span>
  );

  const { data: fetchedItems = [], isLoading } = useQuery({
    queryKey: ['inventory', 'roasted-bulk-group-items', { itemId: group.itemId, roastType: group.roastType }],
    queryFn: () => inventoryService.getRoastedBulkGroupItems(group.itemId, group.roastType),
    enabled: !group.items,
  });

  const localItems: RoastedBulkGroupItem[] = group.items ?? fetchedItems;
  const filteredLocalItems = localItems.filter(item => {
    const resolvedStatus = statusByItemId.get(item.inventoryItemId) ?? item.status;
    return statusFilter === 'all' || resolvedStatus === statusFilter;
  });

  if (isLoading) {
    return <LoadingSpinner text={t('inventory.roastedBulk.loadingLots')} />;
  }

  if (!filteredLocalItems.length) {
    return <div className="text-sm text-gray-500">{t('inventory.roastedBulk.emptyLots')}</div>;
  }

  if (compact) {
    return (
      <div className="space-y-3">
        {filteredLocalItems.map(item => {
          const inventoryItem = items.find(i => i.id === item.inventoryItemId);
          const itemStatus = statusByItemId.get(item.inventoryItemId) ?? item.status;
          return (
            <div
              key={item.lotId}
              className={`border border-gray-200 rounded-md p-3 space-y-2 ${onEditItem && canWrite ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              data-test-id={`inventory-roasted-group-item-row-${item.workOrderId}-${item.lotId}`}
              onClick={() => {
                if (onEditItem && canWrite) {
                  onEditItem(item.inventoryItemId);
                }
              }}
            >
              <div className="flex justify-between text-sm">
                <div>
                  <div className="font-medium">{t('inventory.roastedBulk.woPrefix')} {item.workOrderNumber}</div>
                  <div className="text-xs text-gray-500">
                    {t('inventory.roastedBulk.remaining')}: {formatDecimal(item.remainingQuantity, 3)}
                  </div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>{t('inventory.roastedBulk.in')}: {formatDecimal(item.inputWeight, 3)}</div>
                  <div>{t('inventory.roastedBulk.out')}: {formatDecimal(item.outputWeight, 3)}</div>
                  <div>
                    {t('inventory.roastedBulk.shrink')}:{' '}
                    {item.shrinkagePercent !== null ? `${formatDecimal(item.shrinkagePercent, 2)}%` : '-'}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <div className="text-gray-500">
                  <div>{t('inventory.roastedBulk.expiry')}: {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : t('settingsMcp.values.none')}</div>
                  <div className="mt-1">{renderItemStatus(itemStatus)}</div>
                </div>
                <div className="flex gap-2">
                  {inventoryItem && (
                    <div onClick={(event) => event.stopPropagation()}>
                      <InventoryRoastProfileButton
                        item={inventoryItem}
                        onClick={() => {
                          onOpenRoastProfile({
                            itemId: inventoryItem.id,
                            itemName: inventoryItem.name,
                            workOrderId: item.workOrderId,
                          });
                        }}
                        readOnly={!canWrite}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.workOrder')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.inputKg')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.outputKg')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.shrinkage')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.remaining')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.expiry')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('forms.common.status')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.cogs')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.beanProfile')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.roastProfile')}
            </th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.cuppingProfile')}
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('settingsMcp.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredLocalItems.map(item => {
            const inventoryItem = items.find(i => i.id === item.inventoryItemId);
            const itemStatus = statusByItemId.get(item.inventoryItemId) ?? item.status;
            return (
              <tr
                key={item.lotId}
                data-test-id={`inventory-roasted-group-item-row-${item.workOrderId}-${item.lotId}`}
                className={onEditItem && canWrite ? 'cursor-pointer hover:bg-gray-50' : undefined}
                onClick={() => {
                  if (onEditItem && canWrite) {
                    onEditItem(item.inventoryItemId);
                  }
                }}
              >
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {item.workOrderNumber}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {formatDecimal(item.inputWeight, 3)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {formatDecimal(item.outputWeight, 3)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {formatDecimal(item.shrinkageKg, 3)} {t('inventory.history.unitKg')}
                  <span className="ml-1 text-xs text-gray-500">
                    {item.shrinkagePercent !== null ? `(${formatDecimal(item.shrinkagePercent, 2)}%)` : ''}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {formatDecimal(item.remainingQuantity, 3)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                  {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : t('settingsMcp.values.none')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {renderItemStatus(itemStatus)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center">
                  {typeof item.unitPrice === 'number' ? formatCurrency(item.unitPrice, currencySymbol) : t('settingsMcp.values.none')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center" onClick={(event) => event.stopPropagation()}>
                  {inventoryItem && (
                    <ProfileManager
                      entityType="ItemType"
                      entityId={inventoryItem.id}
                      profileType="greenBean"
                      existingProfile={inventoryItem.greenBeanProfiles?.find(profile => profile.type === 'ItemType')}
                      label={inventoryItem.greenBeanProfiles?.some(profile => profile.type === 'ItemType') ? t('reports.shift.view') : undefined}
                    />
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm text-gray-700" onClick={(event) => event.stopPropagation()}>
                  {inventoryItem && (
                    <InventoryRoastProfileButton
                      item={inventoryItem}
                      onClick={() => {
                        onOpenRoastProfile({
                          itemId: inventoryItem.id,
                          itemName: inventoryItem.name,
                          workOrderId: item.workOrderId,
                        });
                      }}
                      readOnly={!canWrite}
                    />
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-center" onClick={(event) => event.stopPropagation()}>
                  {inventoryItem && (
                    <ProfileManager
                      entityType="ItemType"
                      entityId={inventoryItem.id}
                      profileType="cupping"
                      existingProfile={inventoryItem.cuppingProfiles?.find((profile: CuppingProfile) => profile.type === 'ItemType')}
                      label={inventoryItem.cuppingProfiles?.some((profile: CuppingProfile) => profile.type === 'ItemType') ? t('reports.shift.view') : undefined}
                    />
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenHistory(item.inventoryItemId);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                  >
                    <History className="w-4 h-4" />
                    {t('inventory.roastedBulk.history')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
